import { z } from 'zod'

import type { Creator } from '@/modules/creators/types.ts'
import { apiClientV2 } from '@/utils/axios'
import { dateToString } from '@/utils/helpers'

const RevenueEntrySchema = z.object({
  name: z.string(),
  revenue: z.number(),
})

const ChatterTrackingEntrySchema = z.object({
  minutesActive: z.number(),
  minutesInactive: z.number(),
  grossSoldRevenue: z.number(),
  netSoldRevenue: z.number(),
  grossTipsRevenue: z.number().nullish(),
  netTipsRevenue: z.number().nullish(),
  sendMediaMessages: z.number(),
  boughtMediaMessages: z.number(),
  sendMessages: z.number().nullish(),
  keystrokes: z.number().nullish(),
  totalGrossRevenue: z.number(),
  totalNetRevenue: z.number(),
  averageResponseTime: z.number(),
})

const ActivityStatesDataSchema = z.enum(['ONLINE', 'OFFLINE', 'IDLE'])

const ChatterTrackingUserEntrySchema = z.object({
  userId: z.number().nullish(),
  name: z.string(),
  profilePicture: z.string().nullish(),
  activityState: ActivityStatesDataSchema,
  data: ChatterTrackingEntrySchema,
})

const ChatterTrackingSingleUserResultSchema = z.object({
  timezoneOffset: z.string().nullish(),
  userId: z.number().nullish(),
  name: z.string(),
  profilePicture: z.string().nullish(),
  activityState: ActivityStatesDataSchema,
  data: z.record(ChatterTrackingEntrySchema),
})

const ChatterTrackingEntryWithDateSchema = ChatterTrackingEntrySchema.extend({
  date: z.string(),
})

const ChatterTrackingMetricsSchema = z.record(ChatterTrackingEntrySchema)

const RevenueDataSchema = z.record(z.array(RevenueEntrySchema))

const TotalDataSchema = z.object({
  name: z.string(),
  revenue: z.number(),
})

const ChatterTrackingDataSchema = z.object({
  timezoneOffset: z.string().nullish(),
  dataList: z.array(ChatterTrackingUserEntrySchema),
  revenues: RevenueDataSchema,
  total: z.array(TotalDataSchema),
})

const AllChatterTableTypeSchema = z.object({
  userId: z.number().nullish(),
  name: z.string(),
  profilePicture: z.string().nullish(),
  minutesActive: z.number(),
  minutesInactive: z.number(),
  totalNetRevenue: z.number(),
  averageResponseTime: z.number(),
  sendMessages: z.number(),
  sentPPVs: z.number(),
  boughtPPVs: z.number(),
})

async function findInspectByNameAndDate(
  creatorId: number,
  userId: number | undefined,
  browserName: string | undefined,
  from: Date,
  to: Date,
  hourly: boolean,
): Promise<ChatterTrackingSingleUserResult> {
  const params = {
    start: dateToString(from),
    end: dateToString(to),
    hourly: hourly,
  }
  const response = await apiClientV2.get(
    `creator/${creatorId}/tracking/${browserName ? 'browser' : 'user'}/${browserName ? encodeURIComponent(browserName) : userId}`,
    {
      params,
    },
  )

  return ChatterTrackingSingleUserResultSchema.parse(response.data)
}

async function findLeaderboardInspectByNameAndDate(
  creators: Creator[],
  browserName: string | undefined,
  userId: number | undefined,
  from: Date,
  to: Date,
  hourly: boolean,
): Promise<ChatterTrackingSingleUserResult> {
  const params = `from=${dateToString(from)}&to=${dateToString(
    to,
  )}&hourly=${hourly}&creatorIds=${creators
    .map((creator) => creator.id)
    .join(',')}`
  const response = await apiClientV2.get(
    `creator/leaderboard/${browserName ? 'browser' : 'user'}/${browserName ? encodeURIComponent(browserName) : userId}?${params}`,
  )

  return response.data
}

export const trackingService = {
  findInspectByNameAndDate,
  findLeaderboardInspectByNameAndDate,
}

export type ChatterTrackingUserEntry = z.infer<
  typeof ChatterTrackingUserEntrySchema
>
export type ChatterTrackingMetrics = z.infer<
  typeof ChatterTrackingMetricsSchema
>
export type ChatterTrackingData = z.infer<typeof ChatterTrackingDataSchema>
export type AllChatterTableType = z.infer<typeof AllChatterTableTypeSchema>
export type ChatterTrackingEntryWithDateType = z.infer<
  typeof ChatterTrackingEntryWithDateSchema
>
export type ChatterTrackingSingleUserResult = z.infer<
  typeof ChatterTrackingSingleUserResultSchema
>
